import dayjs from "dayjs";
import React from "react";
import { DatePicker } from "@pd/pd-ui-kit";
import { Control, Controller, DeepMap } from "react-hook-form";
import { FieldError } from "react-hook-form/dist/index.ie11";
import { useTranslation } from "react-i18next";

import { TIndicatorReportsConfigForm } from "~/components/Forms/IndicatorReportsConfigForm";
import { getYearOptionsForSelect } from "~/libs/getDateOptions";
import { TypeGroupDealerIndicatorValues } from "~/schemas/generated/graphql";
import { DISABLED_FUTURE_DATE } from "~/shared/lib/disabledFutureDate";
import { Field } from "~/shared/ui/Field";

import fieldStyles from "~/shared/ui/Field/Field.less";

const DISPLAY_DATE_FORMAT = "DD.MM.YYYY";
// Дата хранится в форме строкой yyyy-MM-dd — так её принимают
// сериализаторы запросов отчёта (new Date(...) в lib)
const STORAGE_DATE_FORMAT = "YYYY-MM-DD";

interface IAdditionFieldResolverProps {
  groupType: TypeGroupDealerIndicatorValues;
  control: Control<TIndicatorReportsConfigForm>;
  errors: DeepMap<TIndicatorReportsConfigForm, FieldError>;
}

const AdditionFieldResolver = ({
  groupType,
  control,
  errors,
}: IAdditionFieldResolverProps) => {
  switch (groupType) {
    case TypeGroupDealerIndicatorValues.Regmen:
      return <ResolveRegManSelectFields control={control} errors={errors} />;
    default:
      return <ResolveDateFields control={control} errors={errors} />;
  }
};

export default AdditionFieldResolver;

type TResolverInnerProps = Omit<IAdditionFieldResolverProps, "groupType">;

/**
 * Резолвер полей RegMen
 * */
const ResolveRegManSelectFields = ({
  control,
  errors,
}: TResolverInnerProps) => {
  const { t } = useTranslation();
  const quarterOptions = [1, 2, 3, 4].map((quarter) => ({
    value: quarter,
    label: String(quarter),
  }));
  const yearOptions = getYearOptionsForSelect().map((year) => ({
    value: year,
    label: String(year),
  }));

  return (
    <>
      <Field
        type="select"
        name="quarter"
        control={control}
        label={t("reports.quarter")}
        options={quarterOptions}
        labelInValue
        error={errors["quarter"]?.value?.message}
      />
      <Field
        type="select"
        name="year"
        control={control}
        label={t("reports.year")}
        options={yearOptions}
        labelInValue
        error={errors["year"]?.value?.message}
      />
    </>
  );
};

/**
 * Резолвер полей Default
 * */
const ResolveDateFields = ({ control, errors }: TResolverInnerProps) => {
  const { t } = useTranslation();

  return (
    <>
      <ReportDateField
        name="dateBegin"
        label={t("reports.dateBegin")}
        control={control}
        error={errors["dateBegin"]?.message}
      />
      <ReportDateField
        name="dateEnd"
        label={t("reports.dateEnd")}
        control={control}
        error={errors["dateEnd"]?.message}
      />
    </>
  );
};

interface IReportDateFieldProps {
  name: "dateBegin" | "dateEnd";
  label: string;
  control: Control<TIndicatorReportsConfigForm>;
  error?: string;
}

/**
 * Поле даты на DatePicker из pd-ui-kit с хранением значения строкой.
 * Разметка label/ошибки повторяет shared/ui/Field.
 * */
const ReportDateField = ({
  name,
  label,
  control,
  error,
}: IReportDateFieldProps) => (
  <Controller
    control={control}
    name={name}
    render={({ value, onChange, onBlur }) => (
      <div className={fieldStyles.field}>
        <span className={fieldStyles.fieldLabel}>{label}</span>
        <DatePicker
          allowClear
          style={{ width: "100%" }}
          placeholder={label}
          format={DISPLAY_DATE_FORMAT}
          disabledDate={DISABLED_FUTURE_DATE}
          value={value ? dayjs(String(value), STORAGE_DATE_FORMAT) : null}
          onChange={(date) =>
            onChange(date ? dayjs(date).format(STORAGE_DATE_FORMAT) : null)
          }
          onBlur={onBlur}
          status={error ? "error" : undefined}
        />
        {error && <span className={fieldStyles.fieldError}>{error}</span>}
      </div>
    )}
  />
);
